import React from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import LinearProgress from '@mui/material/LinearProgress';
import { Box, Typography, Button } from '@mui/material';
import { AdminPanelSettings as AdminIcon } from '@mui/icons-material';
import AuditTable from './AuditTable';

import S3AudithStore from '../Services/S3AudithStore';
import { getUserCustomClaims } from '../Services/getUserClaims';

const LoggedPage = () => {
  const [rawData, setData] = React.useState({
    isLoading: false,
    data: [],
    nextMarker: null,
    nextDateOffset: null,
    thresholdTs: null,
    progress: 0,
    isSearching: false,
  });
  const [isAdmin, setIsAdmin] = React.useState(false);

  // Verificar se o usuário é admin
  React.useEffect(() => {
    let isMounted = true;
    
    const checkAdminStatus = async () => {
      try {
        const claims = await getUserCustomClaims();
        if (isMounted) {
          setIsAdmin(claims.admin === true);
        }
      } catch (error) {
        console.error('Erro ao verificar claims:', error);
      }
    };
    checkAdminStatus();
    
    // Cleanup function para evitar memory leak
    return () => {
      isMounted = false;
    };
  }, []);
  
  // Proteção de rota tratada via ProtectedRoute

  React.useEffect(() => {
    let isMounted = true;
    
    setData((prevState) => ({ ...prevState, isLoading: true }));
    const store = new S3AudithStore();
    store.loadInitial({
      days: 30,
      pageSize: 30,
      maxPages: 1,
      onProgress: (pct) => {
        if (isMounted) {
          setData((prev) => ({ ...prev, progress: Math.round(pct * 100) }));
        }
      },
    }).then((state) => {
      if (isMounted) {
        setData({
          isLoading: false,
          data: state.items,
          nextMarker: state.nextMarker,
          nextDateOffset: state.nextDateOffset ?? null,
          thresholdTs: state.thresholdTs,
          progress: 100,
        });
      }
    });
    
    // Cleanup function para evitar memory leak
    return () => {
      isMounted = false;
    };
  }, []);

  const handleChangePage = async () => {
    if (!rawData.nextMarker && (rawData.nextDateOffset == null)) return;
    setData((prev) => ({ ...prev, isLoading: true }));
    const store = new S3AudithStore();
    store.state = { items: rawData.data, nextMarker: rawData.nextMarker, nextDateOffset: rawData.nextDateOffset, thresholdTs: rawData.thresholdTs, initialized: true };
    const nextState = await store.loadNextPage({
      pageSize: 30,
      onProgress: (pct) => setData((prev) => ({ ...prev, progress: Math.round(pct * 100) })),
    });
    setData({
      isLoading: false,
      data: nextState.items,
      nextMarker: nextState.nextMarker,
      nextDateOffset: nextState.nextDateOffset ?? null,
      thresholdTs: nextState.thresholdTs,
      progress: 100,
    });
  };

  const handleSearch = async (query) => {
    if (!query || String(query).trim() === '') {
      setData((prev) => ({ ...prev, isLoading: true, isSearching: false, progress: 0 }));
      const store = new S3AudithStore();
      const state = await store.loadInitial({ days: 30, pageSize: 30, maxPages: 1, onProgress: (pct) => setData((p) => ({ ...p, progress: Math.round(pct * 100) })) });
      setData({
        isLoading: false,
        data: state.items,
        nextMarker: state.nextMarker,
        nextDateOffset: state.nextDateOffset ?? null,
        thresholdTs: state.thresholdTs,
        progress: 100,
      });
      return;
    }
    setData((prev) => ({ ...prev, isSearching: true, progress: 0 }));
    const store = new S3AudithStore();
    const nextState = await store.search({
      query,
      limit: 500,
      onProgress: (pct) => setData((prev) => ({ ...prev, progress: Math.round(pct * 100) })),
    });
    setData((prev) => ({
      ...prev,
      data: nextState.items,
      nextMarker: null,
      nextDateOffset: null,
      isSearching: false,
      progress: 100,
    }));
  };

  const handleRefresh = async () => {
    setData((prev) => ({ ...prev, isLoading: true, progress: 0 }));
    const store = new S3AudithStore();
    const state = await store.loadInitial({ days: 30, pageSize: 30, maxPages: 1, onProgress: (pct) => setData((p) => ({ ...p, progress: Math.round(pct * 100) })) });
    setData({
      isLoading: false,
      data: state.items,
      nextMarker: state.nextMarker,
      nextDateOffset: state.nextDateOffset ?? null,
      thresholdTs: state.thresholdTs,
      progress: 100,
    });
  };

  return (rawData.isLoading || rawData.isSearching) ? (
    <Box sx={{ width: '90%', position: 'fixed', top: '20%' }}>
      <Box sx={{ mb: 2, px: 4 }}>
        <Typography variant="body2">{rawData.isSearching ? 'Buscando...' : 'Carregando registros...'} {rawData.progress}%</Typography>
        <LinearProgress variant="determinate" value={rawData.progress} />
      </Box>
      <center>
        <CircularProgress />
      </center>
    </Box>
  ) : (
    <Box>
      {/* Indicador de Admin */}
      {isAdmin && (
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            startIcon={<AdminIcon />}
            disabled
          >
            Modo Administrador
          </Button>
        </Box>
      )}
      
      <AuditTable
        data={rawData.data}
        onLoadMore={handleChangePage}
        hasMore={Boolean(rawData.nextMarker || (rawData.nextDateOffset != null))}
        loading={rawData.isLoading}
        onRefresh={handleRefresh}
        onSearch={handleSearch}
      />
    </Box>
  );
};

export default React.memo(LoggedPage);
